import { Alert, Button, Card, Empty, Skeleton, Space, Statistic, Tag, Typography } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { formatSignedSum, formatSum, toAmount, useFamilyBalance } from "../app/balance.ts";
import { ItemList } from "./ItemList.tsx";
import { CreditTransferModal } from "./CreditTransferModal.tsx";

interface Props {
  familyId: string;
  /** balance.manage: offers the sibling credit transfer. */
  canManage: boolean;
}

/** Each child's balance and the family total (read-only; balance.read). Positive is credit, negative is debt. */
export function FamilyBalanceCard({ familyId, canManage }: Props) {
  const { t } = useTranslation();
  const query = useFamilyBalance(familyId);
  const [transferring, setTransferring] = useState(false);

  /** A coloured tag stating credit, debt or settled; the sign is said in words, not only by colour. */
  function balanceTag(value: number) {
    if (value > 0) return <Tag color="green">{t("balance.credit", { amount: formatSum(value) })}</Tag>;
    if (value < 0) return <Tag color="red">{t("balance.debt", { amount: formatSum(value) })}</Tag>;
    return <Tag>{t("balance.settled")}</Tag>;
  }

  const balance = query.data;
  const hasCredit = balance?.students.some((s) => toAmount(s.balance) > 0) ?? false;
  const canTransfer = canManage && (balance?.students.length ?? 0) > 1;

  return (
    <Card
      title={t("balance.title")}
      extra={
        canTransfer && (
          <Button icon={<SwapOutlined />} disabled={!hasCredit} title={hasCredit ? undefined : t("balance.transfer.noCredit")} onClick={() => setTransferring(true)}>
            {t("balance.transfer.open")}
          </Button>
        )
      }
    >
      {query.isPending && <Skeleton active paragraph={{ rows: 3 }} />}
      {query.isError && (
        <Alert type="error" showIcon title={t("balance.loadError")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />
      )}
      {balance && (
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text type="secondary">{t("balance.hint")}</Typography.Text>
          {balance.students.length === 0 ? (
            <Empty description={t("familyDetail.noStudents")} />
          ) : (
            <ItemList
              items={balance.students}
              itemKey={(student) => student.studentId}
              renderItem={(student) => ({
                title: (
                  <Link to={`/students/${student.studentId}`}>
                    {student.lastName} {student.firstName}
                  </Link>
                ),
                actions: [<span key="balance">{balanceTag(toAmount(student.balance))}</span>],
              })}
            />
          )}
          <Space wrap align="center" style={{ width: "100%", justifyContent: "space-between" }}>
            <Statistic title={t("balance.familyTotal")} value={formatSignedSum(toAmount(balance.total))} suffix={t("balance.currency")} />
            {balanceTag(toAmount(balance.total))}
          </Space>
        </Space>
      )}
      {balance && canTransfer && <CreditTransferModal family={balance} open={transferring} onClose={() => setTransferring(false)} />}
    </Card>
  );
}
